import React from 'react';
import {useIntl} from 'react-intl';
import {Text} from '@forecast-it/design-system';
import {StageBandBar} from './StageBandBar';
import {bandForWinChance, type StageBand} from './opportunityStage';

type Props = {
	/** The account's mapping, live from the graph. */
	bands: StageBand[];
	/** The project's current win chance, as a 0..1 fraction. */
	value: number;
	/** Called with the chosen band's win probability, as a 0..1 fraction. */
	onChange: (value: number) => void;
};

/**
 * The win-probability picker, as one segment per stage in the account's mapping.
 *
 * The highlighted segment is the band the current win chance falls in, so a mid-band value — one set
 * from the free percentage field on Project Settings, say — still lights the band it belongs to rather
 * than nothing. Picking a segment emits that band's representative win probability.
 *
 * 0% and 100% are never segments: they are reached through Mark as Lost / Mark as Won, and offering
 * them here would let the picker convert an opportunity as a side effect of choosing a stage.
 */
export const WinProbabilityStageBands = ({bands, value, onChange}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const resolved = bandForWinChance(bands, value);
	// bandForWinChance's own {kind: 'unknown'} already covers this (no segments to resolve a value
	// into) — an empty StageBandBar rendered no explanation at all, indistinguishable from a bar
	// that simply hadn't loaded yet. This is the account having no mapping to pick from (getBands()
	// self-seeds defaults on first read, so in practice this is the bootstrap query itself having
	// failed — see AiProjectPlanningModal's own Alert for that case — rather than a real empty
	// config), so it reads the same as any other "not available" field rather than a broken widget.
	if (resolved.kind === 'unknown') {
		return <Text>{formatMessage({id: 'common.not_available'})}</Text>;
	}
	return (
		<StageBandBar
			value={resolved.kind === 'band' ? resolved.band.id : ''}
			onChange={bandId => {
				const picked = bands.find(band => band.id === bandId);
				if (picked) {
					onChange(picked.winProbability / 100);
				}
			}}
			dataCy="win-probability"
			items={bands.map(band => ({
				value: band.id,
				topText: `${band.winProbability}%`,
				label: band.name,
				dataCy: `win-band-${band.id}`,
			}))}
		/>
	);
};

export default WinProbabilityStageBands;
